import { httpsCallable } from "firebase/functions";
// Vi bruger 'functions' instansen fra jeres centrale konfiguration
import { functions } from "../firebaseConfig"; 
import { ChatMessage } from "../types";

/**
 * NOREWYN SIKKERHEDSSYSTEM (V12.0 - CAMPUS)
 * Debug-optimeret til at diagnosticere forbindelsesfejl i skyen.
 * Alle API-nøgler opbevares sikkert i Firebase Secrets.
 */

const getGeminiCall = async (task: string, payload: any) => {
  // 1. Find ud af hvilken funktion vi skal kalde (Vision vs Text)
  const isVisionTask = [
    'generateillustration', 
    'generatesigilicon', 
    'generateavatar', 
    'generateguideillustration'
  ].includes(task.toLowerCase());
  
  // 2. Brug CAMPUS præfiks (cp_) for at ramme de korrekte Cloud Functions
  const functionName = isVisionTask ? 'cp_callGeminiVision' : 'cp_callGemini';
  
  const call = httpsCallable(functions, functionName);
  
  try {
      const result: any = await call({ task, payload });
      return result.data;
  } catch (error: any) {
      console.error(`[CAMPUS-FEJL - ${task}]:`, error);
      
      // Håndtering af de mest gængse Cloud fejl uden env.process
      if (error.code === 'internal' || error.message?.includes('500')) {
        console.warn("Skyen svarede med en 500-fejl. Tjek API_KEY secret og billing i Google Cloud Console.");
      } else if (error.code === 'permission-denied') {
        console.warn("Adgang nægtet. Din magiske profil mangler de rette segl.");
      } else if (error.code === 'deadline-exceeded') {
        console.warn("Magien tog for lang tid. Portalen lukkede forbindelsen.");
      }
      
      return null;
    }
};

/**
 * GUIDE RESPONS (Den Gamle Ekko)
 * Bruges til interaktion i rummene
 */
export const getMagicalGuideResponse = async (userMessage: string, roomName: string): Promise<string> => {
  const result = await getGeminiCall('guide_response', { userMessage, roomName });
  return result?.text || "Stjernerne er tavse lige nu... forbindelsen til Den Gamle Ekko flimrer.";
};

/**
 * LORE GENERERING
 * Bruges i 'The Forge' til at skabe magiske beskrivelser af lektioner
 */
export const generateMagicalLore = async (title: string, facts: string): Promise<string> => {
  return await getGeminiCall('generateMagicalLore', { title, facts });
};

/**
 * TRIVIA & VIDEN
 * Skaber indhold til opslagstavler og magiske bøger
 */
export const generateFactualKnowledge = async (topic: string): Promise<string> => {
  return await getGeminiCall('generateFactualKnowledge', { topic });
};

export const generateTrivia = async (topic: string): Promise<any> => {
  return await getGeminiCall('generateTrivia', { topic });
};

/**
 * BILLED-MAGI (Vision)
 * Skaber illustrationer til rum og profiler
 */
export const generateIllustration = async (prompt: string): Promise<string> => {
  const b64 = await getGeminiCall('generateIllustration', { prompt });
  return b64 ? `data:image/png;base64,${b64}` : '';
};

export const generateSigilIcon = (title: string) => 
    getGeminiCall('generateSigilIcon', { title });

/**
 * STEMME-MAGI
 * Omdanner tekst til lyd via Cloud-ekkoet
 */
export const speakText = async (text: string, voiceName: string = 'Charon'): Promise<void> => {
  const base64Audio = await getGeminiCall('speakText', { text, voiceName });
  if (base64Audio) {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({sampleRate: 24000});
      const binaryString = atob(base64Audio);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const dataInt16 = new Int16Array(bytes.buffer);
      const frameCount = dataInt16.length;
      const buffer = audioContext.createBuffer(1, frameCount, 24000);
      const channelData = buffer.getChannelData(0);
      for (let i = 0; i < frameCount; i++) {
        channelData[i] = dataInt16[i] / 32768.0;
      }
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start();
      return new Promise((resolve) => {
        source.onended = () => resolve();
        setTimeout(resolve, 500); 
      });
  }
};